import { useEffect, useRef } from "react";
import { reportError } from "@/components/ErrorBoundary";

// Registro do aviso «Sem conexão com o servidor» / «Sua sessão expirou» (Pedro, 23/10/2026: saber se o lojista viu e o
// que aconteceu depois). Vai pelo client_errors, com o prefixo [aviso-conexao]: quando o aviso aparece (uma vez por
// aparição), o «Tentar de novo» (carregou ou falhou de novo) e o «Entrar». Sem rede, o registro fica guardado neste
// navegador e sai quando a conexão volta (ou na próxima vez que o painel abrir), com a hora em que aconteceu.
const CHAVE = "vnz:aviso-conexao-pendentes";
const MAXIMO = 20;

type Pendente = { texto: string; quando: string };

/** O motivo pelo erro da consulta: 401 (sessão), rede (sem resposta, status 0) ou servidor-<status>. */
export function motivoDoAviso(erro: unknown): string {
  const status = (erro as { status?: number } | null)?.status;
  if (status === 401) return "401";
  if (typeof status === "number" && status > 0) return `servidor-${status}`;
  return "rede";
}

function lerFila(): Pendente[] {
  try {
    const v = JSON.parse(localStorage.getItem(CHAVE) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function gravarFila(fila: Pendente[]) {
  try {
    if (fila.length === 0) localStorage.removeItem(CHAVE);
    else localStorage.setItem(CHAVE, JSON.stringify(fila.slice(-MAXIMO)));
  } catch {}
}

function enviar(texto: string): Promise<boolean> {
  const e = new Error(texto);
  e.stack = "";
  return reportError(e, null, "aviso-conexao");
}

let enviandoFila = false;
async function enviarFila() {
  if (enviandoFila) return;
  const fila = lerFila();
  if (fila.length === 0) return;
  enviandoFila = true;
  try {
    const sobra: Pendente[] = [];
    for (const p of fila) {
      const ok = await enviar(`${p.texto} (aconteceu em ${p.quando}, enviado depois)`);
      if (!ok) sobra.push(p);
    }
    // O que entrou na fila enquanto ela era enviada continua lá.
    const novos = lerFila().filter((p) => !fila.some((f) => f.quando === p.quando && f.texto === p.texto));
    gravarFila([...sobra, ...novos]);
  } finally {
    enviandoFila = false;
  }
}

// `semFila`: o registro que sai junto com a troca de página («Entrar»). O envio sobrevive à saída (keepalive), mas a
// resposta não volta, e a fila o mandaria de novo.
export function registrarAviso(texto: string, semFila = false) {
  const quando = new Date().toISOString();
  void enviar(texto).then((ok) => {
    if (!ok && !semFila) gravarFila([...lerFila(), { texto, quando }]);
    else if (ok) void enviarFila();
  });
}

// Durante o «Tentar de novo» a lista sem nada guardado volta a «carregando» e, se falhar, o aviso reaparece: é a
// mesma aparição (o resultado vai no registro da tentativa), não uma nova.
const emTentativa = new Map<string, number>();

/** Uma vez por aparição do aviso (o efeito roda na montagem; o aviso que fica na tela não registra de novo). */
export function useRegistroDoAviso(tela: string, erro: unknown) {
  const feito = useRef(false);
  useEffect(() => {
    if (feito.current) return;
    feito.current = true;
    if (emTentativa.has(tela)) return;
    registrarAviso(`apareceu tela=${tela} motivo=${motivoDoAviso(erro)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** Para telas de erro que não usam a FalhaAoCarregar (a grande do Pedidos): só registra a aparição. */
export function RegistroDoAviso({ tela, erro }: { tela: string; erro: unknown }) {
  useRegistroDoAviso(tela, erro);
  return null;
}

/** «Tentar de novo»: espera a nova busca (refetch do React Query, uma ou várias) e registra o resultado. */
export async function tentarERegistrar(tela: string, erro: unknown, tentar: () => unknown) {
  const marca = Date.now();
  emTentativa.set(tela, marca);
  try {
    const r = await Promise.resolve(tentar());
    const lista = Array.isArray(r) ? r : [r];
    const falhou = lista.some((x) => (x as { isError?: boolean } | null)?.isError === true);
    registrarAviso(`tentar-de-novo tela=${tela} resultado=${falhou ? "falhou-de-novo" : "carregou"} motivo=${motivoDoAviso(erro)}`);
  } finally {
    // O aviso que reaparece monta logo depois da resposta: a marca fica mais um pouco.
    setTimeout(() => { if (emTentativa.get(tela) === marca) emTentativa.delete(tela); }, 2000);
  }
}

/** Chamar uma vez ao abrir o painel: manda o que ficou guardado e escuta a volta da conexão. */
export function iniciarFilaDoAviso() {
  try {
    window.addEventListener("online", () => void enviarFila());
  } catch {}
  void enviarFila();
}
